// FAR 91.411 / 91.413 pitot-static, altimeter and transponder checks.
// Company facts are read from the shared files (lib/site-config, lib/credentials)
// rather than restated here, so a change to the certificate, phone or ratings
// propagates to this page automatically.

import { siteConfig } from '@/lib/site-config'
import { credentials } from '@/lib/credentials'

export const pitotStaticPageDetails = {
  title: 'Pitot-Static, Altimeter & Transponder Checks at KPTK | Michigan Aviation',
  description:
    "FAR 91.411 and 91.413 pitot-static, altimeter and transponder checks every 24 calendar months at Michigan Aviation's FAA Part 145 repair station at KPTK in Waterford, Michigan.",
} as const

export const regulationLinks = {
  far91411: 'https://www.ecfr.gov/current/title-14/part-91/section-91.411',
  far91413: 'https://www.ecfr.gov/current/title-14/part-91/section-91.413',
} as const

/** A fact carries `verified`. Only verified facts with a value render, so an
 *  unconfirmed number can sit here as a named gap instead of being guessed at
 *  on the page. To publish one, fill in `value` and set `verified: true` —
 *  both are required, so flipping the flag alone changes nothing. */
export type QuickFact = {
  label: string
  value: string
  verified: boolean
}

const radioClasses =
  credentials.repairStation.ratings.find((rating) => rating.category === 'Radio')?.classes.join(', ') ?? ''
const instrumentClasses =
  credentials.repairStation.ratings.find((rating) => rating.category === 'Instrument')?.classes.join(', ') ?? ''

export const quickFacts: QuickFact[] = [
  {
    label: 'Regulations',
    value: 'FAR 91.411 (static system, altimeters, altitude reporting) and FAR 91.413 (transponder)',
    verified: true,
  },
  {
    label: 'Interval',
    value: 'Every 24 calendar months, and after certain maintenance',
    verified: true,
  },
  {
    label: 'Repair station',
    value: `${credentials.repairStation.type}, certificate ${credentials.repairStation.certificate}; ratings include Radio ${radioClasses} and Instrument ${instrumentClasses}`,
    verified: true,
  },
  {
    label: 'Location',
    value: `${siteConfig.airport.code}, ${siteConfig.airport.city}, ${siteConfig.airport.state}`,
    verified: true,
  },
  {
    label: 'Book',
    value: `${siteConfig.contact.phone} or ${siteConfig.contact.email}`,
    verified: true,
  },

  // Not confirmed yet. These stay off the page until someone fills in the value
  // and flips the flag. Turnaround, price and maximum test altitude are things
  // an owner plans around, so an estimate here would be read as a commitment.
  { label: 'Typical time on the ground', value: '', verified: false },
  { label: 'Price', value: '', verified: false },
  { label: 'Maximum test altitude available for jets', value: '', verified: false },
  { label: 'RVSM-related checks', value: '', verified: false },
]

export const verifiedQuickFacts = (): QuickFact[] =>
  quickFacts.filter((fact) => fact.verified && fact.value.trim().length > 0)

const isFactVerified = (label: string): boolean =>
  verifiedQuickFacts().some((fact) => fact.label === label)

/** The turnaround FAQ answers a question we cannot answer yet, so it is tied to
 *  the same fact rather than carrying a separate hardcoded estimate. */
export const timeOnGroundVerified = (): boolean => isFactVerified('Typical time on the ground')

export const testCoverage = [
  'Static system leak test',
  'Altimeter accuracy checks across the altitude range being tested',
  'Correspondence check between the altimeter and the altitude encoder',
  'Transponder tests under Part 43 Appendix F',
]

export const bookingDetails = [
  'Tail number and aircraft type',
  'Transponder, encoder and ADS-B equipment installed',
  'The highest altitude you need the system tested to',
  'Date of the last check',
  'Any other avionics or maintenance work you want done in the same visit',
]

type Faq = { question: string; answer: string }

const baseFaqs: Faq[] = [
  {
    question: 'How often are pitot-static and transponder checks required?',
    answer:
      'Every 24 calendar months, plus after certain maintenance described above.',
  },
  {
    question: 'Do VFR pilots need these checks?',
    answer:
      'The 91.411 altimeter and static checks apply to IFR flight in controlled airspace. The 91.413 transponder check applies whenever a transponder is required, VFR or IFR.',
  },
  {
    question: 'Can my A&P do the check?',
    answer:
      'An A&P with an airframe rating can do the static system test only. The altimeter, altitude reporting and transponder tests need a properly rated repair station or the manufacturer.',
  },
  {
    question: 'Can I combine the check with my annual or avionics work?',
    answer:
      'Yes. Avionics and maintenance are on the same field at KPTK, so the work can be scheduled for the same visit.',
  },
  {
    question: 'What happens if my check has lapsed?',
    answer:
      'You cannot fly IFR in controlled airspace until the 91.411 tests are current, and you cannot use a required transponder until the 91.413 test is current.',
  },
  {
    question: 'Is the 30-day VOR check part of this?',
    answer:
      'No. The VOR check under FAR 91.171 is a separate IFR requirement and is not part of the 91.411/91.413 tests.',
  },
]

const turnaroundFaq: Faq = {
  question: 'How long does the check take?',
  answer: quickFacts.find((fact) => fact.label === 'Typical time on the ground')?.value ?? '',
}

/** Built at render time so the visible FAQ and the FAQPage JSON-LD are always
 *  generated from the same list and cannot drift apart. */
export const pitotStaticFaqs = (): Faq[] =>
  timeOnGroundVerified() ? [...baseFaqs, turnaroundFaq] : baseFaqs
